<template>
  <article class="ai-turn ai-turn--assistant ai-turn--streaming">
    <div class="ai-turn__avatar ai-turn__avatar--tracey" aria-hidden="true">
      <v-icon small color="#fff">mdi-creation</v-icon>
    </div>
    <div class="ai-turn__col">
      <!-- Planning / thinking preamble (streamed before any tool call) -->
      <div v-if="thinking" class="ai-turn__bubble ai-turn__bubble--thinking">
        <div class="ai-turn__thinking-label">
          <v-icon size="13" color="#8e8ea0">mdi-lightbulb-outline</v-icon>
          <span>Planning</span>
        </div>
        <div class="ai-turn__text ai-prose ai-prose--thinking" v-html="parsedThinking" />
      </div>

      <!-- Tool steps + streaming answer -->
      <div v-if="toolSteps.length || content || !thinking" class="ai-turn__bubble">
        <div v-if="toolSteps.length || (!content && !thinking)" class="ai-turn__statuses">
          <div
            v-for="(step, idx) in toolSteps"
            :key="idx"
            class="ai-turn__status"
            :class="{ 'ai-turn__status--done': step.done }"
          >
            <v-icon v-if="step.done" size="14" color="#22a06b">mdi-check-circle</v-icon>
            <v-progress-circular v-else indeterminate color="primary" size="14" width="2" />
            <span>{{ step.label }}</span>
          </div>
          <div v-if="!content && !thinking && !toolSteps.length" class="ai-turn__status">
            <span class="ai-turn__typing-dots"><span></span><span></span><span></span></span>
            <span>Thinking</span>
          </div>
        </div>
        <div v-if="content" class="ai-turn__text ai-prose" v-html="parsedContent" />
      </div>
    </div>
  </article>
</template>

<script>
import { parseAssistantMarkdown } from './markdownUtils.js';
import { describeTool } from './toolDescriptions.js';

export default {
  name: 'TraceyAiStreamingRow',
  props: {
    /** Preamble streamed before any tool call ("I'll start by..."). */
    thinking: { type: String, default: '' },
    /** The final answer text (streamed after tools run). */
    content: { type: String, default: '' },
    /** The tool batch currently in progress (spinner). */
    pendingTools: { type: Array, default: () => [] },
    /** Every tool call made so far this turn — so earlier rounds stay visible
     *  instead of being wiped when the next batch or the answer streams in. */
    allToolCalls: { type: Array, default: () => [] },
  },
  computed: {
    parsedContent() {
      return parseAssistantMarkdown(this.content);
    },
    parsedThinking() {
      return parseAssistantMarkdown(this.thinking);
    },
    toolSteps() {
      const all = this.allToolCalls && this.allToolCalls.length ? this.allToolCalls : this.pendingTools;
      if (!all || !all.length) return [];
      const pendingCount = (this.pendingTools || []).length;
      const doneCount = Math.max(0, all.length - pendingCount);
      return all.map((tc, i) => ({
        label: describeTool(tc && tc.name),
        done: i < doneCount,
      }));
    },
  },
};
</script>

<style scoped>
.ai-turn {
  display: flex;
  gap: 12px;
  padding: 8px 24px;
  width: 100%;
  max-width: 88rem;
  margin: 0 auto;
  align-items: flex-start;
}

.ai-turn__avatar {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
}

.ai-turn__avatar--tracey {
  background: linear-gradient(135deg, #12253a 0%, #1e3a5f 100%);
}

.ai-turn__col {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
}

.ai-turn__bubble {
  min-width: 0;
  max-width: 100%;
  padding: 12px 16px;
  border-radius: 16px;
  border-top-left-radius: 4px;
  background: #f7f9fc;
  border: 1px solid #e6ecf3;
}

.ai-turn__bubble--thinking {
  background: #f0f1f4;
  border: 1px dashed #d7dae2;
}

.ai-turn__thinking-label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #8e8ea0;
  margin-bottom: 6px;
}

.ai-turn__text {
  font-size: 15px;
  line-height: 1.65;
  color: #353740;
}

.ai-prose--thinking {
  font-size: 13.5px;
  color: #6e6e80;
}

.ai-turn__statuses {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.ai-turn__statuses:last-child {
  margin-bottom: 0;
}

.ai-turn__status {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #6e6e80;
}

.ai-turn__status--done {
  color: #353740;
}

.ai-turn__typing-dots {
  display: inline-flex;
  gap: 3px;
  align-items: center;
}

.ai-turn__typing-dots span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #8e8ea0;
  animation: ai-dot-pulse 1.2s ease-in-out infinite;
}

.ai-turn__typing-dots span:nth-child(2) {
  animation-delay: 0.15s;
}

.ai-turn__typing-dots span:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes ai-dot-pulse {
  0%, 80%, 100% { opacity: 0.35; transform: scale(0.85); }
  40% { opacity: 1; transform: scale(1); }
}

.ai-prose ::v-deep p {
  margin: 0 0 0.75em;
}

.ai-prose ::v-deep p:last-child {
  margin-bottom: 0;
}

.ai-prose ::v-deep img {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0.75em 0;
  border-radius: 8px;
}

.ai-prose ::v-deep pre {
  overflow-x: auto;
  max-width: 100%;
}

.ai-prose ::v-deep .ai-prose__table-wrap {
  overflow-x: auto;
  margin: 0.75em 0;
  max-width: 100%;
  width: 100%;
}

/* Links in Tracey replies (tenders, buyers, suppliers). !important is needed
   because the global customstyles.css forces every <a> to navy with
   !important, which made links indistinguishable from body text. Scoped to
   .ai-prose so the rest of the app keeps its link styling. */
.ai-prose ::v-deep a,
.ai-prose ::v-deep a:visited {
  color: #ff1463 !important;
  text-decoration: underline !important;
  text-underline-offset: 2px;
  text-decoration-thickness: 1px;
  font-weight: 500;
  cursor: pointer;
}

.ai-prose ::v-deep a:hover,
.ai-prose ::v-deep a:focus-visible {
  color: #c80f4e !important;
  text-decoration-thickness: 2px;
}

.ai-prose ::v-deep table {
  border-collapse: collapse;
  width: max-content;
  min-width: 100%;
  font-size: 13px;
}

.ai-prose ::v-deep th,
.ai-prose ::v-deep td {
  padding: 8px 12px;
  border: 1px solid #e6ecf3;
  max-width: none;
  white-space: normal;
}
</style>
